import { Form, Spin } from "antd"
import { useEffect, useState } from "react"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { serialize } from "object-to-formdata"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updateTrip, getGuideTrip, getCategories, getTripCategories } from "store/trips"
import { CustomForm } from "ui/Form"
import { AdditionalDetails } from "./AdditionalDetails"
import { ExperienceDetails } from "./ExperienceDetails"
import { ExperienceInfo } from "./ExperienceInfo"
import { GuestDetails } from "./GuestDetails"
import { PublishingSettings } from "./PublishingSettings"
import { setFormValues, setFormTouched } from "store/proNewListingForms"

export const ListingDetails = () => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const trip = useSelector(modelSelector("trip"))
  const { licenses_required, licenses_list } = trip
  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [selectedSkillLevels, setSelectedSkillLevels] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const formDetails = useSelector((state) => state.proNewListingForms.details)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
      dispatch(getCategories())
      dispatch(getTripCategories())
    }
  }, [dispatch, id])

  useEffect(() => {
    if (trip) {
      let categories = []
      if (Array.isArray(trip.categories)) {
        categories = trip.categories
      } else if (trip.categories) {
        categories = [trip.categories]
      }
      form.setFieldsValue({
        title: trip.title,
        description: trip.description,
        categories: categories.length ? categories : undefined,
        min_guests: trip.min_guests,
        max_guests: trip.max_guests,
        languages: trip.languages,
        skill_levels: trip.skill_levels,
        included_items: trip.included_items,
        bring_items: trip.bring_items,
        accessibility_enabled: trip.accessibility_enabled,
        alcohol_allowed: trip.alcohol_allowed,
        children_allowed: trip.children_allowed,
        publish_on_guides_site: trip.publish_on_guides_site,
        publish_on_platform: trip.publish_on_platform
      })
    }
  }, [trip, form])

  const onFinish = (callback) => {
    if (!id) return

    form.validateFields().then(() => {
      const formData = createFormData()
      handleFormSubmission(formData, callback)
    })
  }

  const createFormData = () => {
    const formValues = form.getFieldsValue()

    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []

    const payload = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    return serialize({ trip: payload }, { allowEmptyArrays: true })
  }

  const handleFormSubmission = (formData, callback) => {
    setIsLoading(true)
    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("details", true))
        setIsLoading(false)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    const formValues = form.getFieldsValue()
    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const allValues = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    dispatch(setFormValues("details", allValues))
  }, [dispatch, trip, licenses_required, licenses_list, selectedLanguages, selectedSkillLevels, form])

  const handleValuesChange = (_, allValues) => {
    const currentValues = {
      ...formDetails?.values,
      ...allValues
    }

    dispatch(setFormValues("details", currentValues))
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={{
          ...trip,
          licenses_required: licenses_required,
          licenses_list: licenses_list
        }}
        layout="vertical"
        style={{ maxWidth: "984px" }}
      >
        <ExperienceInfo form={form} />
        <GuestDetails licenses_required={licenses_required} form={form} licenses_list={licenses_list} />
        <ExperienceDetails
          form={form}
          setSelectedLanguages={setSelectedLanguages}
          selectedLanguages={selectedLanguages}
          selectedSkillLevels={selectedSkillLevels}
          setSelectedSkillLevels={setSelectedSkillLevels}
        />
        <AdditionalDetails form={form} />
        <PublishingSettings form={form} />
      </CustomForm>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </Spin>
  )
}
